import type { AclEntityMinInfo } from "@eu.mdotm.api/sphere";
import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import { platformToast } from "$root/notification-system/toast";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { type ShareACLEntry, type ShareDialogProps, ShareDialog } from "../ShareDialog";
import { fuzzyFilterSort } from "@mdotm/shadcn/legacy/utils";
import { analyzeAclChanges, processAclWarnings } from "./acl-warnings";
import { mergeMultiEntityPermissions } from "./merge-multi-entity-permissions";
import type { AclProviderResult, EntityMetadata, SpawnShareDialogSmartParams } from "./types";

type SpawnShareDialogParams = Omit<ShareDialogProps, "onClose" | "show">;

function spawnShareDialog(params: SpawnShareDialogParams): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ resolve, show, onHidden }) => (
			<ShareDialog
				{...params}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onSubmitAsync={async (acl) => {
					await params.onSubmitAsync(acl);
					resolve();
				}}
			/>
		)),
	);
}

function sanitizeSingleAcl(customerEntityAcl: Array<RichAccessControl> | undefined): ShareACLEntry[] {
	return (customerEntityAcl ?? []).flatMap((userAcl): [ShareACLEntry] | [] =>
		!userAcl.userId || !userAcl.permissions
			? []
			: [
					{
						...userAcl,
						userId: userAcl.userId,
						permissionState: undefined,
						permissions: userAcl.permissions,
					},
				],
	);
}

export async function spawnShareDialogSmart(params: SpawnShareDialogSmartParams): Promise<SpawnResult<void>> {
	const { header, disclaimerProvider, userId, saveAclHandler, area } = params;

	let sanitizeCustomerEntityAcl: ShareACLEntry[];
	let sanitizeAclLinkedEntities: AclEntityMinInfo[];
	let rawAclResults: Array<AclProviderResult>;
	let entityMetadataList: EntityMetadata[];

	if (params.multiple) {
		const aclResults = await params.customerAndLinkedAclProvider();
		rawAclResults = aclResults;
		entityMetadataList = params.entityMetadata;
		const merged = mergeMultiEntityPermissions(aclResults, area);
		sanitizeCustomerEntityAcl = merged.acl;
		sanitizeAclLinkedEntities = merged.linkedEntities;
	} else {
		const singleResult = await params.customerAndLinkedAclProvider();
		rawAclResults = [singleResult];
		entityMetadataList = [params.entityMetadata];
		sanitizeCustomerEntityAcl = sanitizeSingleAcl(singleResult.customerEntityAcl);
		sanitizeAclLinkedEntities = singleResult.linkedAclEntities ?? [];
	}

	const currentOwnerId = sanitizeCustomerEntityAcl?.find(({ permissions }) => permissions.includes("OWNER"))?.userId;
	if (!currentOwnerId) {
		platformToast({
			icon: "Icon-full-error",
			severity: "error",
			children: "No owner found, unable to share",
		});
		throw new Error("missing owner");
	}

	return spawnShareDialog({
		acl: sanitizeCustomerEntityAcl.filter((user) => user.permissionState === "mixed" || user.permissions.length > 0),
		usersProvider: (query) =>
			fuzzyFilterSort(
				sanitizeCustomerEntityAcl,
				query,
				(user) => `${user.name?.toLowerCase()} ${user.surname?.toLowerCase()} ${user.email?.toLowerCase()}`,
			),
		currentUserId: userId,
		onSubmitAsync: async (acl) => {
			const analysis = await analyzeAclChanges(rawAclResults, entityMetadataList, acl);
			const saveParams = await processAclWarnings(analysis, saveAclHandler);
			if (saveParams) {
				await saveAclHandler(saveParams);
			}
		},
		ownerId: currentOwnerId,
		header,
		disclaimerProvider,
		aclLinkedEntities: sanitizeAclLinkedEntities,
		area,
	});
}
